import {
  useLocalization
} from "./E42DI7CC.jsx";
import {
  IconChevronLeft,
  IconChevronUpDown
} from "./WHJJ2L4B.jsx";

// src/Calendar/Calendar.tsx
import { createSignal, createEffect, splitProps, For, createMemo } from "solid-js";

// src/Calendar/yearRange.ts
var YearRange = {
  General: [(/* @__PURE__ */ new Date()).getFullYear() - 50, (/* @__PURE__ */ new Date()).getFullYear() + 50],
  Birthday: [(/* @__PURE__ */ new Date()).getFullYear() - 125, (/* @__PURE__ */ new Date()).getFullYear() - 18]
};
function getInitialDate(params) {
  if (params.yearRange) {
    const currentYear = /* @__PURE__ */ new Date();
    const [start, end] = params.yearRange ?? YearRange.General;
    if (currentYear.getFullYear() < start) currentYear.setFullYear(start);
    if (currentYear.getFullYear() > end) currentYear.setFullYear(end);
    return createDate(currentYear);
  }
  return createDate();
}
var createDate = (input) => {
  const date = input ? new Date(input) : /* @__PURE__ */ new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};

// src/Calendar/Calendar.tsx
var YearSelector = (props) => {
  const [params, rest] = splitProps(props, ["selected", "onSelect", "yearRange"]);
  const current = createMemo(() => createDate(params.selected).getFullYear());
  const years = () => {
    const _range = params.yearRange ?? YearRange.General;
    const [start, end] = _range;
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  };
  return <div {...rest}>
      <For each={years()}>
        {(year) => <span
    class="aria-selected:bg-primary aria-[selected='false']:hover-allowed:hover:bg-primary/20 cursor-pointer rounded p-2 text-center aria-selected:text-white"
    onClick={() => {
      const newDate = createDate(params.selected);
      newDate.setFullYear(year);
      params.onSelect?.(newDate);
    }}
    aria-selected={year === current()}
  >
            {year}
          </span>}
      </For>
    </div>;
};
var MonthSelector = (props) => {
  const [params, rest] = splitProps(props, ["selected", "onSelect"]);
  const { currentLanguage } = useLocalization();
  const current = createMemo(() => createDate(params.selected).getMonth());
  const months = () => {
    return Array.from({ length: 12 }, (_, i) => i);
  };
  return <div {...rest}>
      <For each={months()}>
        {(month) => <span
    class="aria-selected:bg-primary aria-[selected='false']:hover-allowed:hover:bg-primary/20 cursor-pointer rounded p-2 text-center aria-selected:text-white"
    onClick={() => {
      const newDate = createDate(params.selected);
      newDate.setDate(1);
      newDate.setMonth(month);
      params.onSelect?.(newDate);
    }}
    aria-selected={month === current()}
  >
            {new Date(0, month).toLocaleDateString(currentLanguage(), {
    month: "short"
  })}
          </span>}
      </For>
    </div>;
};
var Calendar = (props) => {
  const [params, rest] = splitProps(props, ["selected", "onSelect", "yearRange", "disabledDate", "defaultMonth"]);
  const { t, currentLanguage } = useLocalization();
  const [focus, setFocus] = createSignal(params.defaultMonth || getInitialDate(params));
  const [selected, setSelected] = createSignal();
  const [isSelectedYM, setIsSelectedYM] = createSignal(false);
  createEffect(() => {
    setSelected(params.selected ? createDate(params.selected) : void 0);
    params.selected && setFocus(createDate(params.selected));
  });
  let ref;
  createEffect(() => {
    if (!isSelectedYM() || ref == null) return;
    ref.querySelector("[aria-selected=true]")?.scrollIntoView({ block: "center" });
  });
  const calendar = () => {
    const year = focus().getFullYear();
    const month = focus().getMonth();
    const selectedDate = selected();
    const totalDays = new Date(year, month + 1, 0).getDate();
    const firstDayOfWeek = new Date(year, month, 1).getDay();
    const lastDayOfWeek = new Date(year, month, totalDays).getDay();
    const lastDayOfPreviousMonth = new Date(year, month, 0).getDate();
    const isToday = (day) => {
      const today = createDate();
      return today.getDate() === day && today.getMonth() === month && today.getFullYear() === year;
    };
    const isSelected = (day) => {
      if (selectedDate == null) return false;
      return selectedDate.getDate() === day && selectedDate.getMonth() === month && selectedDate.getFullYear() === year;
    };
    const temp = [
      ...Array.from({ length: firstDayOfWeek }, (_, i) => ({
        day: lastDayOfPreviousMonth - firstDayOfWeek + i + 1
      })),
      ...Array.from({ length: totalDays }, (_, i) => ({
        day: i + 1,
        active: true,
        selected: isSelected(i + 1),
        today: isToday(i + 1)
      })),
      ...Array.from({ length: 6 - lastDayOfWeek }, (_, i) => ({ day: i + 1 }))
    ];
    return Array.from({ length: temp.length / 7 }, (_, i) => temp.slice(i * 7, i * 7 + 7));
  };
  const handleMonthChange = (inc) => {
    focus().setDate(1);
    focus().setMonth(focus().getMonth() + inc);
    setFocus(createDate(focus()));
  };
  const handleYearMonthSelect = (date, showYM = false) => {
    setFocus(date);
    setIsSelectedYM(showYM);
  };
  const handleDaySelect = (day, e) => {
    const current = new Date(focus());
    if (!day.active) {
      if (day.day < 15) {
        current.setMonth(current.getMonth() + 1);
      } else {
        current.setMonth(current.getMonth() - 1);
      }
    }
    current.setDate(day.day);
    if (params.disabledDate && params.disabledDate(current)) {
      return;
    }
    if (!day.active) {
      handleMonthChange(day.day < 15 ? 1 : -1);
    }
    const newCurrent = new Date(focus());
    newCurrent.setDate(day.day);
    setSelected(createDate(newCurrent));
    params.onSelect?.(createDate(newCurrent));
    e.stopImmediatePropagation();
  };
  const isThisDayDisabled = (day) => {
    if (params.disabledDate) {
      const current = new Date(focus());
      current.setDate(day);
      return params.disabledDate(current);
    }
    return false;
  };
  const isThisDayCanNotSelect = (day) => {
    return !day.active || isThisDayDisabled(day.day);
  };
  return <div {...rest} ref={ref}>
      <div class="flex w-80 items-center justify-between border-b px-6 pb-3 pt-4">
        <button class="hover-allowed:hover:bg-essential-colour/10 rounded transition-colors" onClick={() => handleMonthChange(-1)}>
          <IconChevronLeft />
        </button>
        <h2
    class="hover-allowed:hover:bg-essential-colour/10 flex cursor-pointer items-center rounded px-3 py-1 text-sm font-medium transition-colors"
    onClick={() => setIsSelectedYM((prev) => !prev)}
  >
          {focus().toLocaleDateString(currentLanguage(), {
    month: "long",
    year: "numeric"
  })}
          <IconChevronUpDown class="ml-1 size-3" />
        </h2>
        <button class="hover-allowed:hover:bg-essential-colour/20 rounded transition-colors" onClick={() => handleMonthChange(1)}>
          <IconChevronLeft class="rotate-180" />
        </button>
      </div>
      <div class="flex w-full gap-4 p-6 text-sm" classList={{ hidden: !isSelectedYM() }}>
        <YearSelector
    selected={focus()}
    onSelect={(date) => handleYearMonthSelect(date, true)}
    class="scroll-mask thinscroll grid h-56 w-1/3 gap-1 overflow-y-auto py-2"
    yearRange={params.yearRange}
  />
        <MonthSelector selected={focus()} onSelect={handleYearMonthSelect} class="grid w-2/3 grid-cols-2 gap-1" />
      </div>
      <table class="mx-auto mb-4 mt-2.5 text-xs" classList={{ hidden: isSelectedYM() }}>
        <thead>
          <tr class="text-text-level03 flex">
            <For each={["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]}>
              {(day) => <td class="flex size-10 items-center justify-center uppercase">{t(day)}</td>}
            </For>
          </tr>
        </thead>
        <tbody>
          <For each={calendar()}>
            {(week) => <tr class="flex">
                <For each={week}>
                  {(day, index) => <td
    id={`${props.id}-day-select-${index()}`}
    class="m-1 flex size-8 cursor-pointer items-center justify-center rounded-full"
    classList={{
      "text-faded": isThisDayCanNotSelect(day),
      "bg-essential-colour text-white": day.selected && !isThisDayCanNotSelect(day),
      "hover-allowed:hover:bg-essential-colour/20": !day.selected && !isThisDayCanNotSelect(day),
      "border border-essential-colour": day.today && !isThisDayCanNotSelect(day)
    }}
    onClick={(e) => handleDaySelect(day, e)}
  >
                      {day.day}
                    </td>}
                </For>
              </tr>}
          </For>
        </tbody>
      </table>
    </div>;
};

export {
  YearRange,
  createDate,
  Calendar
};
